/**
 * Safe number formatting utilities
 * Prevents UI crashes from undefined/null values in dashboard metrics
 */

/**
 * Format number with configurable decimal places
 * @param value - Number to format, may be null/undefined
 * @param options - Formatting options
 * @returns Formatted number string or fallback
 */
export function formatNumber(
  value: number | null | undefined,
  options: {
    maximumFractionDigits?: number
    minimumFractionDigits?: number
    fallback?: string
  } = {}
): string {
  const {
    maximumFractionDigits = 2,
    minimumFractionDigits,
    fallback = '—'
  } = options

  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fallback
  }

  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits,
    ...(minimumFractionDigits !== undefined && { minimumFractionDigits })
  }).format(value)
}

/**
 * Safe number formatter with fallback (legacy - use formatNumber instead)
 * @param value - Number to format, may be null/undefined
 * @param digits - Decimal places (default: 2)
 * @param fallback - Fallback text for null/undefined (default: '—')
 * @returns Formatted number string or fallback
 * @deprecated Use formatNumber instead
 */
export function fmtNum(
  value: number | null | undefined, 
  digits = 2, 
  fallback = '—'
): string {
  return formatNumber(value, { maximumFractionDigits: digits, fallback })
}

/**
 * Safe percentage formatter
 * @param value - Percentage value to format
 * @param digits - Decimal places (default: 1)
 * @returns Formatted percentage string or fallback
 */
export function fmtPercent(
  value: number | null | undefined, 
  digits = 1
): string {
  return typeof value === 'number' && !Number.isNaN(value) 
    ? `${value.toFixed(digits)}%` 
    : '—';
}

/**
 * Currency symbols for supported fiat currencies
 * Reference: Bridge API supported currencies
 */
export const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  MXN: '$', // Mexican Peso uses $ symbol
  BRL: 'R$',
  GBP: '£',
} as const;

/**
 * Safe currency formatter with Bridge API precision
 * 
 * Bridge API precision rule: whole cents only (2 decimal places)
 * Truncates to 2 decimal places, does NOT round
 * Reference: https://apidocs.bridge.xyz/platform/additional-information/precision
 * 
 * @param value - Currency value to format
 * @param currency - Currency code (default: 'USD') - supports 'USD', 'EUR', 'MXN', 'BRL'
 * @param options - Formatting options
 * @returns Formatted currency string or fallback
 */
export function fmtCurrency(
  value: number | null | undefined, 
  currency: keyof typeof CURRENCY_SYMBOLS | string = 'USD',
  options: {
    digits?: number;
    fallback?: string;
    showSymbol?: boolean;
    locale?: string;
  } = {}
): string {
  const {
    digits = 2,
    fallback = '—',
    showSymbol = true,
    locale = 'en-US',
  } = options;

  if (typeof value !== 'number' || Number.isNaN(value)) {
    return fallback;
  }

  // Bridge API precision: truncate to whole cents (do NOT round)
  const multiplier = Math.pow(10, digits);
  const truncatedValue = Math.floor(value * multiplier) / multiplier;

  // Format with locale-aware number formatting
  const formattedNumber = truncatedValue.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

  if (!showSymbol) {
    return formattedNumber;
  }

  const symbol = CURRENCY_SYMBOLS[currency.toUpperCase()] || '$';
  return `${symbol}${formattedNumber}`;
}

/**
 * Format amount without currency symbol (for crypto balances)
 * 
 * Bridge API precision: truncate to 2 decimal places
 * 
 * @param value - Amount to format (string or number)
 * @param options - Formatting options
 * @returns Formatted amount string
 */
export function fmtAmount(
  value: string | number | null | undefined,
  options: {
    digits?: number;
    fallback?: string;
    locale?: string;
  } = {}
): string {
  const {
    digits = 2,
    fallback = '0.00',
    locale = 'en-US',
  } = options;

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (typeof numValue !== 'number' || Number.isNaN(numValue)) {
    return fallback;
  }

  // Bridge API precision: truncate (do NOT round)
  const multiplier = Math.pow(10, digits);
  const truncatedValue = Math.floor(numValue * multiplier) / multiplier;

  return truncatedValue.toLocaleString(locale, {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * Safe array normalizer - ensures always returns array
 * @param v - Value that might be null/undefined/not array
 * @returns Array (empty if input was null/undefined)
 */
export const asArray = <T>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);

/**
 * Safe array length - prevents .length crashes on undefined
 * @param v - Array that might be null/undefined
 * @returns Number (0 if input was null/undefined)
 */
export const len = (v: unknown[] | null | undefined): number => (Array.isArray(v) ? v.length : 0);